import type { EditableGroupValue } from "shared-types";

import type { ConstructedDeliverable, MetadataField } from "@/types/types";

import defineSheet from "@/services/excel/defineSheet";
import { buildExcelFileName } from "@/services/excel/excelFileName";
import exportWorkbook from "@/services/excel/exportWorkbook";
import type { ExcelColumn } from "@/services/excel/types";
import {
    METADATA_SHEET_FIXED_HEADERS,
    METADATA_SHEET_NAME,
    type MetadataSheetFixedHeader
} from "@/utils/metadataSheetSchema";

const SHEET_TITLE = "Metadata and reporting label";
const SHEET_SUBTITLE =
    "Edit the values in the cells below, then re-import this sheet to update the metadata and reporting label of your deliverables.\nDo not change the column headers or the order of the columns, as this will break the import process.";

type MetadataExportRow = {
    fixedValues: Record<MetadataSheetFixedHeader, string>;
    groupValueByGroupName: Record<string, string>;
    metadataValueByFieldId: Record<string, string>;
};

function getGroupValueByGroupName(
    deliverable: ConstructedDeliverable,
    editableGroupValues: Record<string, EditableGroupValue>
) {
    return deliverable.combination.reduce<Record<string, string>>((accumulator, entry) => {
        const groupName = Object.keys(entry)[0];

        if (groupName) {
            accumulator[groupName] = editableGroupValues[entry[groupName]!.groupValueId]?.value ?? "";
        }

        return accumulator;
    }, {});
}

function toMetadataExportRow(
    deliverable: ConstructedDeliverable,
    editableGroupValues: Record<string, EditableGroupValue>
): MetadataExportRow {
    return {
        fixedValues: {
            size: `${deliverable.masterTemplate.width}x${deliverable.masterTemplate.height}`,
            language: deliverable.language,
            reportingLabel: deliverable.reportingLabel
        },
        groupValueByGroupName: getGroupValueByGroupName(deliverable, editableGroupValues),
        metadataValueByFieldId: deliverable.metadata.reduce<Record<string, string>>((accumulator, { field, value }) => {
            accumulator[field._id] = value;

            return accumulator;
        }, {})
    };
}

function buildMetadataColumns(
    editableGroupIds: string[],
    metadataFields: MetadataField[],
    canManageMetadata: boolean,
    canChangeReportingLabel: boolean
) {
    const fixedColumns: ExcelColumn<MetadataExportRow>[] = METADATA_SHEET_FIXED_HEADERS.filter(
        header => header !== "reportingLabel" || canChangeReportingLabel
    ).map(header => ({
        key: `fixed:${header}`,
        header,
        role: "label",
        width: header === "reportingLabel" ? 85 : undefined,
        value: row => row.fixedValues[header]
    }));

    const groupColumns: ExcelColumn<MetadataExportRow>[] = editableGroupIds.map(groupName => ({
        key: `group:${groupName}`,
        header: groupName,
        value: row => row.groupValueByGroupName[groupName] ?? ""
    }));

    const metadataFieldColumns: ExcelColumn<MetadataExportRow>[] = canManageMetadata
        ? metadataFields.map(({ _id, name }) => ({
              key: `field:${_id}`,
              header: name,
              value: row => row.metadataValueByFieldId[_id] ?? ""
          }))
        : [];

    return [...fixedColumns, ...groupColumns, ...metadataFieldColumns];
}

export default async function exportMetadataWorkbook({
    campaignName,
    editableGroupIds,
    editableGroupValues,
    deliverables,
    metadataFields,
    canManageMetadata,
    canChangeReportingLabel
}: {
    campaignName: string | null;
    editableGroupIds: string[];
    editableGroupValues: Record<string, EditableGroupValue>;
    deliverables: ConstructedDeliverable[];
    metadataFields: MetadataField[];
    canManageMetadata: boolean;
    canChangeReportingLabel: boolean;
}) {
    await exportWorkbook({
        fileName: buildExcelFileName([campaignName, "metadata"]),
        sheets: [
            defineSheet<MetadataExportRow>({
                name: METADATA_SHEET_NAME,
                title: SHEET_TITLE,
                subtitle: SHEET_SUBTITLE,
                columns: buildMetadataColumns(
                    editableGroupIds,
                    metadataFields,
                    canManageMetadata,
                    canChangeReportingLabel
                ),
                rows: deliverables.map(deliverable => toMetadataExportRow(deliverable, editableGroupValues))
            })
        ]
    });
}
